import { memo, useState, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Building2, Edit2, Trash2, MapPin, Search, X, Clock, Calendar, Sparkles, Timer, Sun, Moon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button, Input, Label } from '@caruso/ui';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth-store';

interface VenueImage {
  url: string;
  isPrimary?: boolean;
}

interface Venue {
  id: string;
  name: string;
  description?: string;
  defaultPrice: number;
  pricingType: string;
  capacity?: number;
  area?: number;
  images?: VenueImage[];
}

interface VenueBookingWizardData {
  // Venue Config
  venueId?: string;
  venueName?: string;
  venuePrice?: number;

  // Presentation Options
  sendImages?: boolean;
  sendDescription?: boolean;
  showPrice?: boolean;
  customOfferMessage?: string;

  // Period Options
  allowAfternoon?: boolean; // Tarde
  allowEvening?: boolean; // Noite
  afternoonStartTime?: string; // Ex: 12:00
  afternoonEndTime?: string; // Ex: 18:00
  eveningStartTime?: string; // Ex: 18:00
  eveningEndTime?: string; // Ex: 23:59

  // Date Options
  dateOptions?: ('today' | 'this_week' | 'next_week' | 'this_month' | 'custom')[];

  // Availability Config
  slotIntervalHours?: number; // Intervalo em horas para sugestões (1, 2, 4, 6, 8)
  maxSlotsToShow?: number; // Quantidade de slots para mostrar (1-5)

  // AI & Messages
  useHumanizedMessages?: boolean;

  // Timeout Config
  timeoutMinutes?: number;
  timeoutNodeId?: string;
}

export const VenueBookingWizardNode = memo(({ data, id }: NodeProps<VenueBookingWizardData>) => {
  const { deleteElements, setNodes } = useReactFlow();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [isEditing, setIsEditing] = useState(false);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);

  // Venue State
  const [venueId, setVenueId] = useState(data.venueId || '');
  const [venueName, setVenueName] = useState(data.venueName || '');
  const [venuePrice, setVenuePrice] = useState(data.venuePrice || 0);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoadingVenues, setIsLoadingVenues] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Presentation State
  const [sendImages, setSendImages] = useState(data.sendImages ?? true);
  const [sendDescription, setSendDescription] = useState(data.sendDescription ?? true);
  const [showPrice, setShowPrice] = useState(data.showPrice ?? true);
  const [customOfferMessage, setCustomOfferMessage] = useState(data.customOfferMessage || '');

  // Period State
  const [allowAfternoon, setAllowAfternoon] = useState(data.allowAfternoon ?? true);
  const [allowEvening, setAllowEvening] = useState(data.allowEvening ?? true);
  const [afternoonStartTime, setAfternoonStartTime] = useState(data.afternoonStartTime || '12:00');
  const [afternoonEndTime, setAfternoonEndTime] = useState(data.afternoonEndTime || '18:00');
  const [eveningStartTime, setEveningStartTime] = useState(data.eveningStartTime || '18:00');
  const [eveningEndTime, setEveningEndTime] = useState(data.eveningEndTime || '23:59');

  // Date Options State
  const [dateOptions, setDateOptions] = useState<string[]>(
    data.dateOptions || ['today', 'this_week', 'next_week', 'this_month', 'custom']
  );

  // Availability State
  const [slotIntervalHours, setSlotIntervalHours] = useState(data.slotIntervalHours || 4);
  const [maxSlotsToShow, setMaxSlotsToShow] = useState(data.maxSlotsToShow || 3);

  // AI State
  const [useHumanizedMessages, setUseHumanizedMessages] = useState(data.useHumanizedMessages ?? true);

  // Timeout State
  const [timeoutMinutes, setTimeoutMinutes] = useState(data.timeoutMinutes || 60);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (isVenueModalOpen) {
      loadVenues();
    }
  }, [isVenueModalOpen, search]);

  const loadVenues = async () => {
    try {
      setIsLoadingVenues(true);
      const params = new URLSearchParams({ active: 'true' });
      if (search) params.append('search', search);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/venues?${params}`, {
        headers: { 'Authorization': `Bearer ${accessToken}` },
      });

      if (!response.ok) throw new Error('Failed to load venues');
      const venuesList = await response.json();
      setVenues(Array.isArray(venuesList) ? venuesList : []);
    } catch (error) {
      console.error('Error loading venues:', error);
      toast.error('Erro ao carregar ambientes');
      setVenues([]);
    } finally {
      setIsLoadingVenues(false);
    }
  };

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };

  const handleSelectVenue = (venue: Venue) => {
    setVenueId(venue.id);
    setVenueName(venue.name);
    setVenuePrice(venue.defaultPrice);
    setIsVenueModalOpen(false);
  };

  const handleClearVenue = () => {
    setVenueId('');
    setVenueName('');
    setVenuePrice(0);
  };

  const toggleDateOption = (option: string) => {
    setDateOptions(prev =>
      prev.includes(option)
        ? prev.filter(o => o !== option)
        : [...prev, option]
    );
  };

  const handleSave = () => {
    if (!venueId || !venueName) {
      toast.error('Selecione um ambiente');
      return;
    }

    if (!allowAfternoon && !allowEvening) {
      toast.error('Selecione pelo menos um período (tarde ou noite)');
      return;
    }

    if (dateOptions.length === 0) {
      toast.error('Selecione pelo menos uma opção de data');
      return;
    }

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              venueId,
              venueName,
              venuePrice,
              sendImages,
              sendDescription,
              showPrice,
              customOfferMessage,
              allowAfternoon,
              allowEvening,
              afternoonStartTime,
              afternoonEndTime,
              eveningStartTime,
              eveningEndTime,
              dateOptions,
              slotIntervalHours,
              maxSlotsToShow,
              useHumanizedMessages,
              timeoutMinutes,
            },
          };
        }
        return node;
      })
    );

    setIsEditing(false);
    toast.success('Wizard de reserva configurado');
  };

  const handleCancel = () => {
    setVenueId(data.venueId || '');
    setVenueName(data.venueName || '');
    setVenuePrice(data.venuePrice || 0);
    setSendImages(data.sendImages ?? true);
    setSendDescription(data.sendDescription ?? true);
    setShowPrice(data.showPrice ?? true);
    setCustomOfferMessage(data.customOfferMessage || '');
    setAllowAfternoon(data.allowAfternoon ?? true);
    setAllowEvening(data.allowEvening ?? true);
    setAfternoonStartTime(data.afternoonStartTime || '12:00');
    setAfternoonEndTime(data.afternoonEndTime || '18:00');
    setEveningStartTime(data.eveningStartTime || '18:00');
    setEveningEndTime(data.eveningEndTime || '23:59');
    setDateOptions(data.dateOptions || ['today', 'this_week', 'next_week', 'this_month', 'custom']);
    setSlotIntervalHours(data.slotIntervalHours || 4);
    setMaxSlotsToShow(data.maxSlotsToShow || 3);
    setUseHumanizedMessages(data.useHumanizedMessages ?? true);
    setTimeoutMinutes(data.timeoutMinutes || 60);
    setIsEditing(false);
  };

  const dateOptionLabels: Record<string, string> = {
    today: 'Hoje',
    this_week: 'Esta semana',
    next_week: 'Próxima semana',
    this_month: 'Este mês',
    custom: 'Outro período',
  };

  return (
    <>
      <div className="bg-gradient-to-br from-emerald-900/90 to-emerald-950 border-2 border-emerald-400/60 rounded-lg p-4 min-w-[320px] shadow-lg shadow-emerald-500/20">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-emerald-400" />

        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-emerald-400 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-emerald-950" />
          </div>
          <div className="flex-1">
            <span className="font-bold text-white">Wizard de Reserva</span>
            <div className="flex items-center gap-1 text-xs text-emerald-300">
              <Sparkles className="w-3 h-3" />
              Nó Inteligente
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-emerald-400 hover:text-white transition-colors p-1"
              title="Editar wizard"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {venueName ? (
            <>
              <div className="flex items-center gap-2 p-2 bg-emerald-800/50 rounded border border-emerald-600/30">
                <MapPin className="w-4 h-4 text-emerald-400" />
                <span className="flex-1 text-sm text-white font-semibold">{venueName}</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-1 text-emerald-300">
                  {sendImages && <span>📷 Fotos</span>}
                  {sendDescription && <span>📝 Descrição</span>}
                </div>
                <div className="flex items-center gap-1 text-emerald-300 justify-end">
                  {allowAfternoon && <Sun className="w-3 h-3" />}
                  {allowEvening && <Moon className="w-3 h-3" />}
                </div>
              </div>

              <div className="text-xs text-emerald-300/80 p-2 bg-emerald-800/30 rounded">
                <div className="flex items-center gap-1 mb-1">
                  <Clock className="w-3 h-3" />
                  <span>Intervalo: {slotIntervalHours}h | Até {maxSlotsToShow} opções</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>{dateOptions.map(o => dateOptionLabels[o]).join(', ')}</span>
                </div>
              </div>

              {useHumanizedMessages && (
                <div className="flex items-center gap-1 text-xs text-purple-300">
                  <Sparkles className="w-3 h-3" />
                  <span>Mensagens humanizadas com IA</span>
                </div>
              )}

              <div className="flex items-center gap-1 text-xs text-orange-300">
                <Timer className="w-3 h-3" />
                <span>Timeout: {timeoutMinutes} min</span>
              </div>
            </>
          ) : (
            <div className="text-emerald-300/50 text-sm text-center py-4">
              Clique para configurar o wizard de reserva
            </div>
          )}
        </div>

        {/* Saídas com handles posicionados */}
        <Handle
          type="source"
          position={Position.Right}
          id="success"
          className="!w-3 !h-3 !bg-emerald-400"
          style={{ top: '40%' }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="timeout"
          className="!w-3 !h-3 !bg-yellow-400"
          style={{ top: '70%' }}
        />

        {/* Labels das saídas */}
        <div className="absolute right-0 top-0 h-full pointer-events-none pr-6">
          <span className="absolute text-[9px] text-emerald-400 font-medium whitespace-nowrap" style={{ top: '40%', transform: 'translateY(-50%)' }}>Sucesso</span>
          <span className="absolute text-[9px] text-yellow-400 font-medium whitespace-nowrap" style={{ top: '70%', transform: 'translateY(-50%)' }}>Timeout</span>
        </div>
      </div>

      {/* Dialog de Configuração Principal */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-neutral-900 border-emerald-500/30 text-white min-w-[900px] max-w-6xl w-[98vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" />
              Configurar Wizard de Reserva
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Configure todas as etapas do fluxo de reserva inteligente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Seção 1: Ambiente */}
            <div className="p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                1. Ambiente
              </h3>

              {venueName ? (
                <div className="flex items-center gap-2 p-3 bg-neutral-800/50 border border-neutral-700 rounded-lg">
                  <MapPin className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{venueName}</p>
                    <p className="text-xs text-brand-ivory/70">R$ {venuePrice.toFixed(2)}</p>
                  </div>
                  <button
                    onClick={handleClearVenue}
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  onClick={() => setIsVenueModalOpen(true)}
                  variant="outline"
                  className="w-full justify-start border-neutral-700 text-white hover:bg-neutral-800"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Pesquisar ambiente
                </Button>
              )}
            </div>

            {/* Seção 2: Apresentação */}
            <div className="p-4 bg-blue-500/5 rounded-lg border border-blue-500/20">
              <h3 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                📤 2. Apresentação do Ambiente
              </h3>

              <div className="space-y-3">
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendImages}
                      onChange={(e) => setSendImages(e.target.checked)}
                      className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-emerald-500"
                    />
                    <span className="text-sm text-brand-ivory">Enviar fotos (até 3)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendDescription}
                      onChange={(e) => setSendDescription(e.target.checked)}
                      className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-emerald-500"
                    />
                    <span className="text-sm text-brand-ivory">Incluir descrição</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPrice}
                      onChange={(e) => setShowPrice(e.target.checked)}
                      className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-emerald-500"
                    />
                    <span className="text-sm text-brand-ivory">Exibir valor</span>
                  </label>
                </div>

                <div>
                  <Label className="text-brand-ivory/80 text-sm">Mensagem personalizada (opcional)</Label>
                  <textarea
                    value={customOfferMessage}
                    onChange={(e) => setCustomOfferMessage(e.target.value)}
                    placeholder="Ex: Temos uma proposta especial para você! Nosso {{nome_ambiente}} está disponível..."
                    className="w-full mt-1 p-3 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none text-sm"
                    rows={3}
                  />
                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Variáveis: {'{{nome_ambiente}}'}, {'{{preco_ambiente}}'}, {'{{nome_cliente}}'}
                  </p>
                </div>
              </div>
            </div>

            {/* Seção 3: Períodos */}
            <div className="p-4 bg-orange-500/5 rounded-lg border border-orange-500/20">
              <h3 className="text-orange-400 font-semibold mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                3. Períodos Disponíveis
              </h3>

              <div className="grid grid-cols-2 gap-4">
                {/* Tarde */}
                <div className={`p-3 rounded-lg border ${allowAfternoon ? 'bg-orange-500/10 border-orange-500/30' : 'bg-neutral-800/30 border-neutral-700'}`}>
                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={allowAfternoon}
                      onChange={(e) => setAllowAfternoon(e.target.checked)}
                      className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-orange-500"
                    />
                    <Sun className="w-4 h-4 text-orange-400" />
                    <span className="text-sm font-medium text-white">Tarde</span>
                  </label>
                  {allowAfternoon && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <Label className="text-xs text-brand-ivory/60">Início</Label>
                        <Input
                          type="time"
                          value={afternoonStartTime}
                          onChange={(e) => setAfternoonStartTime(e.target.value)}
                          className="bg-neutral-800 border-neutral-600 text-white text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-brand-ivory/60">Fim</Label>
                        <Input
                          type="time"
                          value={afternoonEndTime}
                          onChange={(e) => setAfternoonEndTime(e.target.value)}
                          className="bg-neutral-800 border-neutral-600 text-white text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Noite */}
                <div className={`p-3 rounded-lg border ${allowEvening ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-neutral-800/30 border-neutral-700'}`}>
                  <label className="flex items-center gap-2 cursor-pointer mb-2">
                    <input
                      type="checkbox"
                      checked={allowEvening}
                      onChange={(e) => setAllowEvening(e.target.checked)}
                      className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-indigo-500"
                    />
                    <Moon className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-medium text-white">Noite</span>
                  </label>
                  {allowEvening && (
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <Label className="text-xs text-brand-ivory/60">Início</Label>
                        <Input
                          type="time"
                          value={eveningStartTime}
                          onChange={(e) => setEveningStartTime(e.target.value)}
                          className="bg-neutral-800 border-neutral-600 text-white text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-brand-ivory/60">Fim</Label>
                        <Input
                          type="time"
                          value={eveningEndTime}
                          onChange={(e) => setEveningEndTime(e.target.value)}
                          className="bg-neutral-800 border-neutral-600 text-white text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Seção 4: Opções de Data */}
            <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
              <h3 className="text-purple-400 font-semibold mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                4. Opções de Data
              </h3>

              <div className="flex flex-wrap gap-2">
                {Object.entries(dateOptionLabels).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => toggleDateOption(key)}
                    className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                      dateOptions.includes(key)
                        ? 'bg-purple-500 text-white'
                        : 'bg-neutral-800 text-brand-ivory/60 hover:bg-neutral-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-brand-ivory/50 mt-2">
                O cliente escolherá entre estas opções. "Outro período" permite entrada livre que será interpretada pela IA.
              </p>
            </div>

            {/* Seção 5: Disponibilidade */}
            <div className="p-4 bg-cyan-500/5 rounded-lg border border-cyan-500/20">
              <h3 className="text-cyan-400 font-semibold mb-3 flex items-center gap-2">
                ⏰ 5. Configuração de Disponibilidade
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-brand-ivory/80 text-sm">Intervalo entre sugestões</Label>
                  <select
                    value={slotIntervalHours}
                    onChange={(e) => setSlotIntervalHours(parseInt(e.target.value))}
                    className="w-full mt-1 bg-neutral-800 border border-neutral-600 text-white rounded px-3 py-2 text-sm"
                  >
                    <option value={1}>1 hora</option>
                    <option value={2}>2 horas</option>
                    <option value={4}>4 horas</option>
                    <option value={6}>6 horas</option>
                    <option value={8}>8 horas</option>
                  </select>
                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Espaçamento entre horários sugeridos
                  </p>
                </div>

                <div>
                  <Label className="text-brand-ivory/80 text-sm">Máximo de opções</Label>
                  <select
                    value={maxSlotsToShow}
                    onChange={(e) => setMaxSlotsToShow(parseInt(e.target.value))}
                    className="w-full mt-1 bg-neutral-800 border border-neutral-600 text-white rounded px-3 py-2 text-sm"
                  >
                    <option value={1}>1 opção</option>
                    <option value={2}>2 opções</option>
                    <option value={3}>3 opções</option>
                    <option value={4}>4 opções</option>
                    <option value={5}>5 opções</option>
                  </select>
                  <p className="text-xs text-brand-ivory/50 mt-1">
                    Quantidade de horários apresentados
                  </p>
                </div>
              </div>
            </div>

            {/* Seção 6: IA */}
            <div className="p-4 bg-pink-500/5 rounded-lg border border-pink-500/20">
              <h3 className="text-pink-400 font-semibold mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                6. Inteligência Artificial
              </h3>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useHumanizedMessages}
                  onChange={(e) => setUseHumanizedMessages(e.target.checked)}
                  className="w-4 h-4 rounded border-neutral-600 bg-neutral-800 text-pink-500"
                />
                <span className="text-sm text-brand-ivory">Humanizar mensagens com IA</span>
              </label>
              <p className="text-xs text-brand-ivory/50 mt-2 ml-6">
                A IA irá interpretar respostas do cliente e gerar mensagens mais naturais e personalizadas.
              </p>
            </div>

            {/* Seção 7: Timeout */}
            <div className="p-4 bg-yellow-500/5 rounded-lg border border-yellow-500/20">
              <h3 className="text-yellow-400 font-semibold mb-3 flex items-center gap-2">
                <Timer className="w-4 h-4" />
                7. Timeout
              </h3>

              <div>
                <Label className="text-brand-ivory/80 text-sm">Tempo máximo de espera por resposta</Label>
                <select
                  value={timeoutMinutes}
                  onChange={(e) => setTimeoutMinutes(parseInt(e.target.value))}
                  className="w-full mt-1 bg-neutral-800 border border-neutral-600 text-white rounded px-3 py-2 text-sm"
                >
                  <option value={15}>15 minutos</option>
                  <option value={30}>30 minutos</option>
                  <option value={60}>1 hora</option>
                  <option value={120}>2 horas</option>
                  <option value={240}>4 horas</option>
                  <option value={480}>8 horas</option>
                  <option value={1440}>24 horas</option>
                </select>
                <p className="text-xs text-brand-ivory/50 mt-2">
                  Se o cliente não responder dentro deste tempo, o fluxo seguirá pela saída "Timeout" (amarela).
                </p>
              </div>
            </div>

            {/* Saídas do Nó */}
            <div className="p-4 bg-purple-500/5 rounded-lg border border-purple-500/20">
              <h3 className="text-purple-400 font-semibold mb-3">Saídas do Nó:</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                  <span className="text-emerald-400 font-medium">Sucesso</span>
                  <span className="text-brand-ivory/60">- Cliente selecionou data/horário com sucesso</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <span className="text-yellow-400 font-medium">Timeout</span>
                  <span className="text-brand-ivory/60">- Cliente não respondeu no tempo</span>
                </div>
              </div>
            </div>

            {/* Funcionamento */}
            <div className="p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <h3 className="text-emerald-400 font-semibold mb-2">Como funciona:</h3>
              <ol className="text-xs text-brand-ivory/60 space-y-1 list-decimal list-inside">
                <li>O nó apresenta o ambiente ao cliente (fotos, descrição, mensagem)</li>
                <li>Pergunta o período preferido (tarde/noite)</li>
                <li>Pergunta sobre a data desejada (hoje, semana, mês, etc.)</li>
                <li>Se "outro período", a IA interpreta a resposta livre do cliente</li>
                <li>Consulta disponibilidade e apresenta até {maxSlotsToShow} opções</li>
                <li>Todas as mensagens são humanizadas pela IA</li>
                <li>Conecte este nó ao nó "Confirmar Reserva" para finalizar</li>
              </ol>
            </div>

            {/* Botões */}
            <div className="flex gap-2 justify-end pt-2">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
              >
                Salvar Configuração
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Pesquisa de Ambientes */}
      <Dialog open={isVenueModalOpen} onOpenChange={setIsVenueModalOpen}>
        <DialogContent className="bg-neutral-900 border-emerald-500/30 text-white w-[95vw] max-w-[800px] max-h-[85vh] flex flex-col p-0 gap-0">
          <div className="p-6 pb-4">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-emerald-400" />
                Selecionar Ambiente
              </DialogTitle>
            </DialogHeader>

            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-ivory/50" />
              <Input
                placeholder="Pesquisar ambientes..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-10 bg-neutral-800/50 border-neutral-700 text-white placeholder:text-neutral-500"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 min-h-0">
            <div className="space-y-2 pb-4">
              {isLoadingVenues ? (
                <div className="text-center py-8 text-brand-ivory/50">Carregando ambientes...</div>
              ) : venues.length === 0 ? (
                <div className="text-center py-8 text-brand-ivory/50">Nenhum ambiente encontrado</div>
              ) : (
                venues.map((venue) => {
                  const primaryImage = venue.images?.find(img => img.isPrimary) || venue.images?.[0];
                  return (
                    <button
                      key={venue.id}
                      onClick={() => handleSelectVenue(venue)}
                      className="w-full flex items-center gap-4 p-4 bg-neutral-800/50 hover:bg-neutral-800 border border-neutral-700 hover:border-emerald-400/50 rounded-lg transition-all text-left group"
                    >
                      {primaryImage ? (
                        <div className="w-16 h-16 flex-shrink-0 rounded border border-neutral-700 overflow-hidden bg-neutral-700">
                          <img src={primaryImage.url} alt={venue.name} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-16 h-16 flex items-center justify-center bg-neutral-700 rounded border border-neutral-600 flex-shrink-0">
                          <MapPin className="w-7 h-7 text-emerald-400/50" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-medium text-white group-hover:text-emerald-400 transition-colors">
                          {venue.name}
                        </p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <p className="text-sm text-emerald-400 font-semibold">
                            R$ {venue.defaultPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            <span className="text-xs text-brand-ivory/50 ml-1">
                              /{venue.pricingType === 'daily' ? 'dia' : 'hora'}
                            </span>
                          </p>
                          {venue.capacity && (
                            <span className="text-sm text-brand-ivory/50">• {venue.capacity} pessoas</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="p-6 pt-4 border-t border-neutral-700 flex justify-end">
            <Button
              onClick={() => setIsVenueModalOpen(false)}
              variant="outline"
              className="border-neutral-700 text-white hover:bg-neutral-800"
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

VenueBookingWizardNode.displayName = 'VenueBookingWizardNode';
